import { createDispatchHook } from 'react-redux'
import { applyMiddleware, combineReducers, compose, createStore, Store } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'

import { IAction } from '@/redux/actions/action-helpers'
import { RootTypes } from '@/redux/actions/actions.types'
import adminLocations from '@/redux/reducers/admin-locations.reducer'
import adminUsers from '@/redux/reducers/admin-users.reducer'
import airlines from '@/redux/reducers/airlines.reducer'
import airports from '@/redux/reducers/airports.reducer'
import auth from '@/redux/reducers/auth.reducer'
import carRates from '@/redux/reducers/car-rates.reducer'
import { companyCarProgramsReducer } from '@/redux/reducers/company-car-programs.reducer'
import { companyFlightProgramsReducer } from '@/redux/reducers/company-flight-programs.reducer'
import { companyHotelProgramsReducer } from '@/redux/reducers/company-hotel-programs.reducer'
import { companyInvoiceProfilesReducer } from '@/redux/reducers/company-invoice-profiles.reducer'
import companyTypeModal from '@/redux/reducers/company-type-modal.reducer'
import countries from '@/redux/reducers/countries.reducer'
import countryPOSReducer from '@/redux/reducers/country-pos.reducer'
import { customerSuccessConsultantReducer } from '@/redux/reducers/customerSuccessConsultant.reducer'
import dailyAllowancesPolicyReducer from '@/redux/reducers/daily-allowances-policy.reducer'
import departments from '@/redux/reducers/departments.reducer'
import { essentialTravelInformationReducer } from '@/redux/reducers/essential-travel-information.reducer'
import expensePolicyReducer from '@/redux/reducers/expense-policy.reducer'
import expenseReportsReducer from '@/redux/reducers/expense-reports.reducer'
import expensesReducer from '@/redux/reducers/expenses.reducer'
import fareRules from '@/redux/reducers/fare-rules.reducer'
import homepage from '@/redux/reducers/homepage.reducer'
import hotelsInfo from '@/redux/reducers/hotels-info.reducer'
import hotelsRates from '@/redux/reducers/hotels-rates.reducer'
import { industriesReducer } from '@/redux/reducers/industries.reducer'
import itinerary from '@/redux/reducers/itinerary.reducer'
import liveUserLocations from '@/redux/reducers/live-user-locations.reducer'
import loading from '@/redux/reducers/loading.reducer'
import mileageRatesReducer from '@/redux/reducers/mileage-rates.reducer'
import { modalErrorReducer } from '@/redux/reducers/modal-error.reducer'
import myCompany from '@/redux/reducers/my-company.reducer'
import myUserRoles from '@/redux/reducers/my-user-roles.reducer'
import { packagesAndBillingSettingsReducer } from '@/redux/reducers/packages-and-billing-settings.reducer'
import packagesUsages from '@/redux/reducers/packages-usage.reducer'
import packages from '@/redux/reducers/packages.reducer'
import recentSearches from '@/redux/reducers/recent-searches.reducer'
import reports from '@/redux/reducers/reports.reducer'
import roles from '@/redux/reducers/roles.reducer'
import searchResults from '@/redux/reducers/search-results.reducer'
import searches from '@/redux/reducers/searches.reducer'
import seatmaps from '@/redux/reducers/seatmap.reducer'
import { selectedDocumentsForTripsReducer } from '@/redux/reducers/selected-documents-for-trips.reducer'
import support from '@/redux/reducers/support.reducer'
import taxAndVatRates from '@/redux/reducers/tax-and-vat-rates.reducer'
import travelPolicy from '@/redux/reducers/travel-policy.reducer'
import travelers from '@/redux/reducers/travelers.reducer'
import tripApprovals from '@/redux/reducers/trip-approvals.reducer'
import tripsGaps from '@/redux/reducers/trip-gaps.reducer'
import { tripOrdersReducer } from '@/redux/reducers/trip-orders.reducer'
import { tripPurposesReducer } from '@/redux/reducers/trip-purposes-reducer'
import tripRequests from '@/redux/reducers/trip-requests.reducer'
import trips from '@/redux/reducers/trips.reducer'
import userPermissions from '@/redux/reducers/user-permissions.reducer'
import userPOS from '@/redux/reducers/user-pos.reducer'
import analyticsMiddleware from '@/redux/stores/analytics-middleware'
//Middleware imports should be last to avoid import shenanigans in tests
//TODO: Deal with order of imports
import requestMiddleware from '@/redux/stores/request-middleware'

import NewHotelExperienceReducer from '../reducers/new-hotel-experience.reducer'
import { ApplicationState } from './types'

const appliedMiddlewares = applyMiddleware(requestMiddleware, analyticsMiddleware)

const appReducer = combineReducers({
	auth,
	trips,
	travelers,
	adminUsers,
	adminLocations,
	airlines,
	airports,
	itinerary,
	userPermissions,
	roles,
	travelPolicy,
	searchResults,
	loading,
	myUserRoles,
	myCompany,
	hotelsRates,
	hotelsInfo,
	reports,
	countries,
	support,
	departments,
	searches,
	tripsGaps,
	recentSearches,
	seatmaps,
	fareRules,
	packages,
	packagesUsages,
	liveUserLocations,
	homepage,
	tripRequests,
	tripApprovals,
	carRates,
	companyTypeModal,
	taxAndVatRates,
	userPOS,
	posList: countryPOSReducer,
	industries: industriesReducer,
	companyInvoiceProfiles: companyInvoiceProfilesReducer,
	packagesAndBillingSettings: packagesAndBillingSettingsReducer,
	tripPurposes: tripPurposesReducer,
	essentialTravelInformation: essentialTravelInformationReducer,
	selectedDocumentsForTrips: selectedDocumentsForTripsReducer,
	customerSuccessConsultant: customerSuccessConsultantReducer,
	companyFlightPrograms: companyFlightProgramsReducer,
	companyHotelPrograms: companyHotelProgramsReducer,
	companyCarPrograms: companyCarProgramsReducer,
	dailyAllowancesPolicy: dailyAllowancesPolicyReducer,
	expenses: expensesReducer,
	expensePolicy: expensePolicyReducer,
	expenseReports: expenseReportsReducer,
	mileageRates: mileageRatesReducer,
	tripOrders: tripOrdersReducer,
	newhotelExperienceUI: NewHotelExperienceReducer,
	modalError: modalErrorReducer,
})

function rootReducer(state: ApplicationState | undefined, action: IAction): ApplicationState {
	return appReducer(action.type === RootTypes.ClearStore ? action.payload : state, action)
}

let store: Store<ApplicationState, IAction>

export const useDispatch = createDispatchHook<ApplicationState, IAction>()

export function createApplicationStore(
	initialState: ApplicationState,
	composeMiddlewares: typeof compose | typeof composeWithDevTools = compose,
) {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error - todo migrate Standard Expense to Zod like Mileage
	store = createStore(rootReducer, initialState, composeMiddlewares(appliedMiddlewares))
	return store
}

export function getStore() {
	return store
}

export function getStoreState(): ApplicationState {
	return getStore().getState()
}
